import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { withRouter } from 'react-router-dom';
import api from 'api';
import toast from 'react-hot-toast';
import { connect } from 'react-redux';
import actions from 'actions';
import Room from './Room';
import Lobby from './Lobby';
import { getVideo } from './video';


const useVideoControls = () => {
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [minimised, setMinimised] = useState(false);

  return {
    muted,
    setMuted,
    cameraOff,
    setCameraOff,
    minimised,
    setMinimised,
  };
};

const VideoChat = ({
  user, selectedVideoChat, closeVideoCall, thread, history, onVideoCallCreated,
}) => {
  const [token, setToken] = useState(null);
  const [inLobby, setInLobby] = useState(true);
  const [localTracks, setLocalTracks] = useState(null);
  const [localTracksRequested, setLocalTracksRequested] = useState(false);
  const videoControls = useVideoControls();
  const { muted, cameraOff } = videoControls;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = useMemo(() => new URLSearchParams(window.location.search), [window.location.search]);
  const userId = user?._id;
  const currentCallId = params.get('call');

  const updateQueryParams = useCallback((isEnded = false) => {
    const queryParams = new URLSearchParams({ call: selectedVideoChat }).toString();
    history.replace({ search: isEnded ? '' : (`?${queryParams}`) });
  }, [history, selectedVideoChat]);

  const handleLogout = useCallback(() => {
    localTracks && localTracks.video && localTracks.video.stop();
    localTracks && localTracks.audio && localTracks.audio.stop();
    setLocalTracks(null);
    closeVideoCall(selectedVideoChat);
    updateQueryParams(true);
    setInLobby(true);
    setToken(null);
  }, [closeVideoCall, selectedVideoChat, updateQueryParams, localTracks]);

  useEffect(() => {
    if (selectedVideoChat && userId) {
      if (localTracksRequested) return;

      setLocalTracksRequested(true);
      getVideo().then((Video) => {
        Video.createLocalTracks({
          audio: true,
          video: { width: 640 },
        }).then((newTracks) => {
          const tracks = {
            audio: newTracks.find((t) => t.kind === 'audio'),
            video: newTracks.find((t) => t.kind === 'video'),
          };
          setLocalTracks(tracks);
        });
      })

      api.users.getAccessToken(userId, { roomName: selectedVideoChat }, (data) => {
        setToken(data.token);
      }, () => {
        toast.error('Unable to create video chat.');
        handleLogout();
      });
    } else {
      localTracks && localTracks.video && localTracks.video.stop();
      localTracks && localTracks.audio && localTracks.audio.stop();
      setLocalTracks(null);
      setLocalTracksRequested(false);
    }
  }, [userId, selectedVideoChat, token, localTracks, inLobby, localTracksRequested, handleLogout]);

  useEffect(() => {
    if (muted) {
      localTracks && localTracks.audio && localTracks.audio.disable();
    } else {
      localTracks && localTracks.audio && localTracks.audio.enable();
    }
  }, [muted, localTracks]);

  useEffect(() => {
    if (cameraOff) {
      localTracks && localTracks.video && localTracks.video.disable();
    } else {
      localTracks && localTracks.video && localTracks.video.enable();
    }
  }, [cameraOff, localTracks]);

  useEffect(() => {
    if (currentCallId && selectedVideoChat !== currentCallId) {
      onVideoCallCreated(currentCallId);
    }
  }, [currentCallId, onVideoCallCreated, selectedVideoChat]);

  if (!user) return null;
  if (selectedVideoChat === null) return null;
  if (token === null) return null;
  if (!thread) return null;
  return (
    <>
      {(
        inLobby
          ? <Lobby localTracks={localTracks} thread={thread} videoControls={videoControls} setInLobby={setInLobby} handleLogout={handleLogout} />
          : <Room localTracks={localTracks} videoControls={videoControls} token={token} roomName={selectedVideoChat} handleLogout={handleLogout} />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.user, threads: state.messages.messageThreads, thread: state.messages.messageThreads.find((t) => t._id === state.messages.selectedVideoChat), selectedVideoChat: state.messages.selectedVideoChat,
});

const mapDispatchToProps = (dispatch) => ({
  closeVideoCall: (thread) => dispatch(actions.messages.closeVideoCall(thread)),
  onVideoCallCreated: (thread) => dispatch(actions.messages.createVideoCall(thread)),
});

const VideoChatContainer = (connect(
  mapStateToProps, mapDispatchToProps,
)(VideoChat));

export default withRouter(VideoChatContainer);
